import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataApiResponse, DataCollectionApiResponse } from './types';
import apis from '#store/api';

const LOCATIONS_KEY = 'locations';

export interface Location {
  id?: string;
  name?: string;  
  address?: string;
  latitude?: number;
  longitude?: number;
  incidentId?: string;
}

interface GetLocationsParams {
  name?: string;
  skip?: number;
  take: number;  
  incidentId?: string;
  includeOrganizationLocations?: boolean;
}

interface GetLocationByIdParams {
  id: string;
}

interface DeleteLocationParams {
  id: string;
}

interface CreateLocationParams {
  location: Location;
}

// Fetch locations list
export const useLocations = (params: GetLocationsParams) => {
  return useQuery({
    queryKey: [LOCATIONS_KEY, params],
    queryFn: async () => {
      const response = await apis.locations.getLocationsList({ params });
      return response.data as DataCollectionApiResponse<Location>;
    },
  });
};

// Fetch locations with infinite loading
export const useInfiniteLocations = (params: Omit<GetLocationsParams, 'skip'>, enabled?: boolean) => {
  return useInfiniteQuery<
    DataCollectionApiResponse<Location>,
    Error,
    InfiniteData<DataCollectionApiResponse<Location>>,
    [string, string, GetLocationsParams],
    number
  >({
    queryKey: [LOCATIONS_KEY, 'infinite', params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apis.locations.getLocationsList({ 
        params: {
          ...params,
          skip: pageParam * params.take
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < params.take) return undefined;
      return allPages.length;
    },
    enabled: enabled ?? true
  });
};

// Fetch location by id
export const useLocationById = (params: GetLocationByIdParams) => {
  return useQuery({
    queryKey: [LOCATIONS_KEY, params],
    queryFn: async () => {
      if (!params.id) {
        return { item: undefined } as unknown as DataApiResponse<Location>;
      }
      const response = await apis.locations.getLocationById({ data: params });
      return response.data as DataApiResponse<Location>;
    },
    enabled: !!params.id
  });
};



// Create location
export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: CreateLocationParams) => {
      const response = await apis.locations.createLocation({ data: params });
      return response.data as DataApiResponse<Location>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_KEY] });
    },
  });
};

// Update location
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: CreateLocationParams) => {
      const response = await apis.locations.updateLocation({ data: params });
      return response.data as DataApiResponse<Location>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_KEY] });
    },
  });
};

// Delete location
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: DeleteLocationParams) => {
      await apis.locations.deleteLocation({ data: params });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_KEY] });
    },
  });
};

